import React from "react";
import { Box, Container, Icon, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import WaitlistCTA from "./WaitlistCTA.js";

/**
 * Highlight token. Inline colored/tinted span used inside value-card bodies
 * to pull the reader's eye to the strongest phrase per card.
 */
const H = ({ children }) => (
  <Box
    component="span"
    sx={{
      bgcolor: "rgba(32, 156, 51, 0.15)",
      color: "#14532d",
      fontWeight: 700,
      px: 0.5,
      py: "1px",
      borderRadius: 0.5,
    }}
  >
    {children}
  </Box>
);

const values = [
  {
    num: "01",
    icon: "balance",
    title: "Every school gets what the best-funded schools have.",
    body: (
      <>
        Dedicated instructional coaches + differentiated materials used to be a{" "}
        <H>private-school luxury</H>. Maestro's job is to make that{" "}
        <H>the baseline for every school</H>.
      </>
    ),
  },
  {
    num: "02",
    icon: "lock",
    title: "Ethical AI. Data + privacy first.",
    body: (
      <>
        <H>Student data stays with the school</H>. We're clear about where AI
        belongs (analysis, generation, scaffolding) and where it doesn't (
        <H>evaluating students, replacing teacher judgment</H>).
      </>
    ),
  },
  {
    num: "03",
    icon: "gps_fixed",
    title: "Meet students where they are.",
    body: (
      <>
        <H>Every student can grow</H> if the materials meet them at their level.
        Cookie-cutter curriculum doesn't move the students who need it most.{" "}
        <H>Ours does.</H>
      </>
    ),
  },
  {
    num: "04",
    icon: "school",
    title: "Teachers are experts, not resources.",
    body: (
      <>
        The best product decisions come from teachers.{" "}
        <H>Every workflow is theirs; every skill is built with their voice.</H>{" "}
        Keeping the best teachers in classrooms means treating them as
        decision-makers.
      </>
    ),
  },
  {
    num: "05",
    icon: "autorenew",
    title: "Improve every week. Show the work.",
    body: (
      <>
        Continuous feedback loops (weekly coaching calls, skill iteration,
        year-over-year copy tweaks) are how Maestro operates and how it works.{" "}
        <H>We publish what worked. We publish what didn't.</H> Every classroom's
        data is the starting point for the next one.
      </>
    ),
    wide: true,
  },
];

const Values = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id="values"
      sx={{ py: { xs: 8, md: 14 }, bgcolor: theme.palette.paper.primary }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Our values
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.text.primary,
          }}
        >
          What{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            we're aligned on
          </Box>{" "}
          before we start.
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: theme.palette.text.secondary,
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Whether you're a teacher, a school leader, or a district partner, this
          is what you're signing up for with Maestro.
        </Typography>

        <Box
          sx={{
            mt: 8,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
          }}
        >
          {values.map((v) => (
            <Box
              key={v.num}
              sx={{
                gridColumn: {
                  xs: "auto",
                  md: v.wide ? "span 2" : "auto",
                },
                bgcolor: theme.palette.paper.primary,
                border: `1px solid ${theme.palette.text.tertiary}`,
                borderRadius: 2.5,
                p: 4,
                position: "relative",
                overflow: "hidden",
                transition: "border-color 0.15s, transform 0.15s",
                "&:hover": {
                  borderColor: theme.palette.success.main,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                  letterSpacing: "0.1em",
                }}
              >
                {v.num}
              </Typography>
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  position: "absolute",
                  top: 28,
                  right: 28,
                  fontSize: 40,
                  color: theme.palette.success.main,
                  opacity: 0.4,
                }}
              >
                {v.icon}
              </Icon>
              <Typography
                component="h3"
                sx={{
                  fontSize: "1.375rem",
                  fontWeight: 800,
                  mt: 1.5,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.25,
                }}
              >
                {v.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  color: theme.palette.text.secondary,
                  mt: 1.75,
                  lineHeight: 1.55,
                }}
              >
                {v.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <WaitlistCTA />
      </Container>
    </Box>
  );
};

export default Values;
